import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { Group as GroupM } from 'shell/models/group-profile';
import { Accordion, Typography, IconButton, Tooltip, Grid } from '@mui/material';
import { ExpandMore, Visibility as VisibilityIcon } from '@mui/icons-material';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import ModalFunctionality from './modal-functionality';
import { HeadCell } from 'shell/components/shared/table/types';
import { GroupVM } from 'system/view-models/group';
import { PmiButton } from 'shell/components/shared/button';
import AccordionDetails from 'system/components/groups/form/accordion-details';
import TableGroup from './table';

const sortList = (list: Array<GroupVM>) =>
  list.sort((a, b) => {
    const aEntity = a.entity || '';
    const bEntity = b.entity || '';

    if (aEntity < bEntity) {
      return -1;
    }

    if (a.description < b.description) {
      return -1;
    }

    return 1;
  });

export const headGroups: HeadCell<GroupVM, keyof GroupVM>[] = [
  {
    id: 'id',
    label: 'ID',
  },
  {
    id: 'entity',
    label: 'Entità',
  },
  {
    id: 'description',
    label: 'Descrizione',
  },
  {
    id: 'action',
    label: 'Azione',
  },
];

type FunctionalityModal = {
  open: boolean;
  id?: string;
  checked?: boolean;
};

interface GroupDetailProps {
  onChange: (groups: Array<GroupVM>) => void;
  groups: Array<GroupVM>;
  disabled?: boolean;
  insert?: boolean;
}

const GroupComponent = ({
  onChange,
  groups,
  disabled,
  insert,
}: GroupDetailProps): React.ReactElement => {
  const [open, setOpen] = useState<boolean>(!insert);
  const [openModal, setOpenModal] = useState<FunctionalityModal>({ open: false });

  const {
    data,
    get: getGroups,
    error,
  } = useHttpFetch<HttpResponseList<GroupM>>(`${__MANAGEMENT_API_URL__}/gruppi`, {
    data: {},
  });

  const groupsSaved: Array<GroupVM> = useMemo(() => {
    const operations = data?.payload;
    if (!operations || !!error) {
      return [];
    }
    const list: Array<GroupVM> = operations.list
      .map((group) => {
        const findGroup = groups.find((element) => element.id === group.key.codice.toString());
        const newGroup = {
          id: group.key.codice.toString(),
          description: group.nome,
          institutionCode: group.codiceEnte,
          event: group.evento,
          entity: group.entita,
          action: (
            <Tooltip arrow title={'Visualizza funzionalità gruppo'}>
              <span>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpenModal({
                      open: true,
                      id: group.key.codice.toString(),
                      checked: Boolean(findGroup),
                    });
                  }}
                  aria-label="Visualizza elemento"
                  aria-haspopup="true"
                  style={{
                    padding: '4px',
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </span>
            </Tooltip>
          ),
        };

        const isSelectedGroup = !!groups.find(
          (operation) => operation.id === newGroup.id.toString()
        );

        return {
          ...newGroup,
          checked: isSelectedGroup,
        };
      })
      .filter((item) => item.checked)
      .map((item) => ({ ...item, checked: false }));

    return sortList(list);
  }, [data, groups]);

  const groupsNotSelected: Array<GroupVM> = useMemo(() => {
    const operations = data?.payload;
    if (!operations || !!error) {
      return [];
    }

    const list: Array<GroupVM> = operations.list
      .map((group) => {
        const findGroup = groups.find((element) => element.id === group.key.codice.toString());
        const newGroup = {
          id: group.key.codice.toString(),
          description: group.nome,
          institutionCode: group.codiceEnte,
          event: group.evento,
          entity: group.entita,
          action: (
            <Tooltip arrow title={'Visualizza funzionalità gruppo'}>
              <span>
                <IconButton
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpenModal({
                      open: true,
                      id: group.key.codice.toString(),
                      checked: Boolean(findGroup),
                    });
                  }}
                  aria-label="Visualizza elemento"
                  aria-haspopup="true"
                  style={{
                    padding: '4px',
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </span>
            </Tooltip>
          ),
        };

        const isSelectedGroup = !!groups.find(
          (operation) => operation.id === newGroup.id.toString()
        );

        return {
          ...newGroup,
          checked: isSelectedGroup,
        };
      })
      .filter((item) => !item.checked);

    return sortList(list);
  }, [data, groups]);

  useEffect(() => {
    getGroups();
  }, []);

  const [groupsSavedState, setGroupsSavedState] = useState<Array<GroupVM>>(groupsSaved);

  const [groupsNotSelectedState, setGroupsNotSelectedState] =
    useState<Array<GroupVM>>(groupsNotSelected);

  useEffect(() => {
    setGroupsSavedState(groupsSaved);
  }, [groupsSaved]);

  useEffect(() => {
    setGroupsNotSelectedState(groupsNotSelected);
  }, [groupsNotSelected]);

  const onCheckPressSaved = useCallback(
    (groupSelected) => {
      const groupsNew = groupsSavedState.slice().map((group) =>
        group.id === groupSelected.id
          ? {
              ...group,
              checked: !group.checked,
            }
          : group
      );

      setGroupsSavedState(groupsNew);
    },
    [setGroupsSavedState, groupsSavedState]
  );

  const onCheckPressNotSelected = useCallback(
    (groupSelected) => {
      const groupsNew = groupsNotSelectedState.slice().map((group) =>
        group.id === groupSelected.id
          ? {
              ...group,
              checked: !group.checked,
            }
          : group
      );

      setGroupsNotSelectedState(groupsNew);
    },
    [setGroupsNotSelectedState, groupsNotSelectedState]
  );

  const onSelectAllNotSelected = useCallback(
    (selected) => {
      const groupsNew = groupsNotSelectedState.slice().map((group) => ({
        ...group,
        checked: selected,
      }));

      setGroupsNotSelectedState(groupsNew);
    },
    [setGroupsNotSelectedState, groupsNotSelectedState]
  );

  const onSelectAllSaved = useCallback(
    (selected) => {
      const groupsNew = groupsSavedState.slice().map((group) => ({
        ...group,
        checked: selected,
      }));

      setGroupsSavedState(groupsNew);
    },
    [setGroupsSavedState, groupsSavedState]
  );

  const handleCheckedRight = useCallback(() => {
    const groupsNotSelectedOld = groupsNotSelectedState.slice().filter((item) => item.checked);

    const groupsSavedUpdated = groupsSavedState.concat(groupsNotSelectedOld);

    const newNotSelectedGroups = groupsNotSelectedState.slice().filter((item) => !item.checked);

    setGroupsNotSelectedState(sortList(newNotSelectedGroups));

    setGroupsSavedState(sortList(groupsSavedUpdated));

    onChange(sortList(groupsSavedUpdated));
  }, [
    onChange,
    setGroupsSavedState,
    setGroupsNotSelectedState,
    groupsSavedState,
    groupsNotSelectedState,
  ]);

  const handleCheckedLeft = useCallback(() => {
    const groupsSavedOld = groupsSavedState.slice().filter((item) => item.checked);

    const groupsNotSelectedUpdated = groupsNotSelectedState.concat(groupsSavedOld);

    setGroupsNotSelectedState(sortList(groupsNotSelectedUpdated));

    const newSavedGroups = groupsSavedState.slice().filter((item) => !item.checked);

    setGroupsSavedState(sortList(newSavedGroups));

    onChange(sortList(newSavedGroups));
  }, [
    onChange,
    setGroupsSavedState,
    setGroupsNotSelectedState,
    groupsSavedState,
    groupsNotSelectedState,
  ]);

  return (
    <div className="pmi-pt-2">
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls="panel-groups-content"
          id="panel-groups-header"
          classes={{
            root: 'pmi-flex-row-reverse',
          }}
          expandIcon={<ExpandMore />}
        >
          <Typography component="h6">Lista gruppi</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1} justifyContent="center">
            <Grid item xs>
              <Typography component="h6">Disponibili</Typography>
              <TableGroup
                minWidth={600}
                head={headGroups}
                disabled={disabled}
                insert={insert}
                body={groupsNotSelectedState?.filter(() => !disabled) || []}
                onCheckPress={onCheckPressNotSelected}
                onSelectAll={onSelectAllNotSelected}
              />
            </Grid>
            <Grid item display="flex">
              <Grid container direction="column" justifyContent="center" display="flex">
                <PmiButton
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedRight}
                  disabled={groupsNotSelectedState.length === 0 || disabled}
                  aria-label="move selected right"
                >
                  &gt;
                </PmiButton>
                <PmiButton
                  sx={{ my: 0.5 }}
                  variant="outlined"
                  size="small"
                  onClick={handleCheckedLeft}
                  disabled={groupsSavedState.length === 0 || disabled}
                  aria-label="move selected left"
                >
                  &lt;
                </PmiButton>
              </Grid>
            </Grid>
            <Grid item xs>
              <Typography component="h6">Scelte</Typography>
              <TableGroup
                minWidth={600}
                head={headGroups}
                disabled={disabled}
                insert={insert}
                body={
                  groupsSavedState.map((item) => ({
                    ...item,
                    checked: disabled ? true : item.checked,
                  })) || []
                }
                onCheckPress={onCheckPressSaved}
                onSelectAll={onSelectAllSaved}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      {openModal.open ? (
        <ModalFunctionality
          open={openModal.open}
          onClose={(value) => setOpenModal({ open: value })}
          id={openModal.id}
          checked={openModal.checked}
        />
      ) : undefined}
    </div>
  );
};
GroupComponent.displayName = 'Group detail component';

const Group = memo(GroupComponent);

export default Group as typeof GroupComponent;
